// The source area photos are 2–3 MB full-res JPGs. vite-imagetools resizes +
// recompresses them at build time (down to ~1280px, q70 → ~10× smaller) and
// returns a single optimized URL string. Kept as JPG (not webp) and a single
// URL so the same value stays valid for the OG/<Seo image> tag as well as the
// hero and mini-cards. `as=url` guarantees a string import.
import santaTeresaImg from "@/assets/areas/santa-teresa.jpg?w=1280&quality=70&as=url";
import playaCarmenImg from "@/assets/areas/playa-carmen.jpg?w=1280&quality=70&as=url";
import malPaisImg from "@/assets/areas/mal-pais.jpg?w=1280&quality=70&as=url";
import montezumaImg from "@/assets/areas/montezuma.jpg?w=1280&quality=70&as=url";
import cabuyaImg from "@/assets/areas/cabuya.jpg?w=1280&quality=70&as=url";
import hermosaImg from "@/assets/areas/hermosa.jpg?w=1280&quality=70&as=url";
import santiagoImg from "@/assets/areas/santiago.jpg?w=1280&quality=70&as=url";
import cobanoImg from "@/assets/areas/cobano.jpg?w=1280&quality=70&as=url";
import sanIsidroImg from "@/assets/areas/san-isidro.jpg?w=1280&quality=70&as=url";
import manzanilloImg from "@/assets/areas/manzanillo.jpg?w=1280&quality=70&as=url";

// Placeholder for areas of newer cities that don't yet have real photography.
// Admins upload the real picture per city in Admin → Site assets → Areas
// (site_assets key `areas.<slug>.hero`); the values here are the fallback.
const placeholderAreaImg = "/placeholder.svg";

export interface AreaData {
  slug: string;
  name: string;
  shortDescription: string;
  tags: string[];
  image: string;
  coordinates: [number, number];
  location: {
    bullets: string[];
  };
  knownFor: string[];
  bestFor: string;
  vibe: string;
  access: string;
  /** Optional per-area SEO meta description (geography only, no brand). */
  seoDescription?: string;
  /** On-page "About {area}" prose (paragraphs split on \n\n). */
  seoText: string;
  /**
   * Optional explicit SEO title. Usually omitted, AreaPage composes a
   * tenant-branded title from the area name + the active tenant, so a single
   * area never hardcodes one city's brand.
   */
  seoTitle?: string;
}

// NOTE: `areas` is the code catalog across every tenant. Since the areas table
// (public.areas, Admin → Areas) it is only the seed and the fallback: pages read
// the database first and use this list when the table has no rows for a city.
// Slugs are unique across the whole list.
export const areas: AreaData[] = [
  // ---- Santa Teresa (santa-teresa) ----------------------------------------
  {
    slug: "cabuya",
    name: "Cabuya",
    shortDescription: "Cabuya is a very small, low-key village near the southern tip of the peninsula, known for nature, privacy, and the famous cemetery island.",
    tags: ["Remote", "Quiet", "Nature"],
    image: cabuyaImg,
    coordinates: [-85.0900, 9.6000],
    location: { bullets: ["~10–15 min from Montezuma", "~25–35 min from Santa Teresa", "On the southern tip of the Nicoya Peninsula"] },
    knownFor: ["Nature and privacy", "The famous cemetery island", "Long-term, quiet stays"],
    bestFor: "Long-term residents seeking solitude",
    vibe: "Remote, peaceful, untouched",
    access: "25–35 min from Santa Teresa",
    seoDescription: "Find rental homes in Cabuya, Nicoya Peninsula, Costa Rica. Quiet jungle retreats and private properties for long-term stays.",
    seoText: "Cabuya is a small and peaceful village located near the southern tip of the Nicoya Peninsula. Surrounded by jungle and ocean, it offers a slower pace of life and a strong connection to nature.\n\nIt is best known for its unique cemetery island, which can be accessed during low tide. Cabuya is ideal for long-term stays, retreats, and travelers seeking privacy and tranquility.",
  },
  {
    slug: "cobano",
    name: "Cóbano",
    shortDescription: "Cóbano is the main local town on the peninsula, offering supermarkets, banks, and services in a non-touristy, practical Costa Rican environment.",
    tags: ["Local", "Practical", "Residential"],
    image: cobanoImg,
    coordinates: [-85.1000, 9.6700],
    location: { bullets: ["~10–15 min from Santa Teresa", "~15 min from Montezuma", "Inland on the peninsula"] },
    knownFor: ["Supermarkets, banks, and services", "Local Costa Rican lifestyle", "Non-touristy environment"],
    bestFor: "Budget-conscious long-term residents",
    vibe: "Practical, local, affordable",
    access: "10–15 min from Santa Teresa",
    seoDescription: "Find rental homes in Cóbano, Nicoya Peninsula, Costa Rica. Affordable properties in the main service town of the peninsula.",
    seoText: "Cóbano is the commercial center of the southern Nicoya Peninsula, providing essential services like supermarkets, banks, pharmacies, and government offices. Unlike the coastal tourist areas, Cóbano offers an authentic Costa Rican town experience.\n\nRental properties in Cóbano are typically more affordable, making it a practical choice for long-term residents who want easy access to services while being just a short drive from the beaches.",
  },
  {
    slug: "playa-hermosa",
    name: "Playa Hermosa",
    shortDescription: "Playa Hermosa is a peaceful beach area just north of Santa Teresa, featuring a long, wide beach with fewer crowds and spacious beachfront properties.",
    tags: ["Quiet", "Beachfront", "Spacious"],
    image: hermosaImg,
    coordinates: [-85.1700, 9.6500],
    location: { bullets: ["~5–10 min north of Santa Teresa", "Along a long, wide stretch of beach", "Slightly removed from the main town"] },
    knownFor: ["Fewer crowds", "Spacious beachfront properties", "Relaxed atmosphere"],
    bestFor: "Families and beach lovers seeking space",
    vibe: "Quiet, spacious, relaxed",
    access: "5–10 min north of Santa Teresa",
    seoDescription: "Browse monthly rental homes in Playa Hermosa, Nicoya Peninsula, Costa Rica. Beachfront villas and surf houses for long-term stays.",
    seoText: "Playa Hermosa lives up to its name, 'Beautiful Beach', with a long stretch of golden sand and consistent surf. Located just north of Santa Teresa, it offers a more relaxed atmosphere with stunning beachfront properties.\n\nWith fewer crowds and spacious homes, Playa Hermosa is ideal for families, couples, and anyone seeking a quieter coastal lifestyle while staying close to Santa Teresa's vibrant scene.",
  },
  {
    slug: "mal-pais",
    name: "Mal País",
    shortDescription: "Mal País is a quieter coastal area just south of Santa Teresa, known for luxury villas, ocean views, and a peaceful, rocky coastline.",
    tags: ["Quiet", "Luxury", "Nature"],
    image: malPaisImg,
    coordinates: [-85.1530, 9.5900],
    location: { bullets: ["Just south of Santa Teresa", "~5–10 min from Playa Carmen", "On the southwestern tip of the peninsula"] },
    knownFor: ["Luxury villas and ocean views", "Peaceful atmosphere", "Rocky coastline and sunsets"],
    bestFor: "Couples, families, and those seeking privacy",
    vibe: "Quiet, upscale, serene",
    access: "5–10 min south of Playa Carmen",
    seoDescription: "Browse monthly rental homes in Mal País, Nicoya Peninsula, Costa Rica. Beachfront villas, surf houses, and apartments for long-term stays.",
    seoText: "Mal País is the rugged, refined end of the Nicoya Peninsula. Known for its rocky coastline, incredible sunsets, and laid-back atmosphere, it's a haven for those seeking luxury and solitude.\n\nThe area offers stunning ocean-view properties, from cliffside villas to secluded jungle homes. With easy access to Santa Teresa's amenities, Mal País provides the perfect balance of privacy and convenience.",
  },
  {
    slug: "manzanillo",
    name: "Manzanillo",
    shortDescription: "Manzanillo is a laid-back coastal village south of Santa Teresa, known for its wide sandy beach, dramatic sunsets, and horseback rides along the shore.",
    tags: ["Beach", "Quiet", "Sunsets"],
    image: manzanilloImg,
    coordinates: [-85.1400, 9.5800],
    location: { bullets: ["South of Mal País", "~15–20 min from Santa Teresa", "On the southwestern coast of the peninsula"] },
    knownFor: ["Wide sandy beach", "Horseback riding at sunset", "Quiet, end-of-the-road feel"],
    bestFor: "Beach lovers and those seeking solitude",
    vibe: "Remote, peaceful, scenic",
    access: "15–20 min south of Santa Teresa",
    seoDescription: "Find rental homes in Manzanillo, Nicoya Peninsula, Costa Rica. Beachfront properties and quiet retreats near Santa Teresa.",
    seoText: "Manzanillo is a small, tranquil beach village located south of Mal País on the Nicoya Peninsula. With a wide stretch of sand, stunning sunsets, and a quiet end-of-the-road atmosphere, it offers one of the most peaceful coastal experiences in the area.\n\nPopular for horseback riding along the shore and long beach walks, Manzanillo appeals to travelers and long-term residents seeking a slower pace of life while remaining within easy reach of Santa Teresa's restaurants, shops, and surf.",
  },
  {
    slug: "montezuma",
    name: "Montezuma",
    shortDescription: "Montezuma is a bohemian beach town on the southeastern side of the peninsula, famous for waterfalls, jungle hikes, and an artistic vibe.",
    tags: ["Nature", "Bohemian", "Lively"],
    image: montezumaImg,
    coordinates: [-85.0680, 9.6560],
    location: { bullets: ["On the southeastern side of the peninsula", "~20–30 min from Santa Teresa", "Close to Cabo Blanco Nature Reserve"] },
    knownFor: ["Waterfalls and jungle hikes", "Artistic and alternative vibe", "Small-town nightlife"],
    bestFor: "Nature lovers, artists, and backpackers",
    vibe: "Bohemian, artistic, adventurous",
    access: "20–30 min from Santa Teresa",
    seoDescription: "Find monthly rental homes in Montezuma, Nicoya Peninsula, Costa Rica. Jungle retreats and beach homes near waterfalls and nature reserves.",
    seoText: "Montezuma is a charming bohemian beach town located on the southeastern coast of the Nicoya Peninsula. Famous for its stunning waterfalls, lush jungle surroundings, and artistic community, it offers a unique alternative to the surf-centric towns further west.\n\nWith proximity to Cabo Blanco Nature Reserve and a vibrant small-town atmosphere, Montezuma is ideal for travelers and long-term residents who value nature, creativity, and community.",
  },
  {
    slug: "playa-carmen",
    name: "Playa Carmen",
    shortDescription: "Playa Carmen is the central hub of Santa Teresa, offering walking distance to shops, gyms, and restaurants with a lively but balanced atmosphere.",
    tags: ["Central", "Walkable", "Convenient"],
    image: playaCarmenImg,
    coordinates: [-85.1630, 9.6200],
    location: { bullets: ["In the middle of Santa Teresa", "Walking distance to shops, gyms, and restaurants", "~5–10 min from Mal País"] },
    knownFor: ["Convenience and accessibility", "Best area without needing a car", "Lively but balanced atmosphere"],
    bestFor: "Those who want walkability and convenience",
    vibe: "Central, social, easy-going",
    access: "Central, walking distance to everything",
    seoDescription: "Browse monthly rental homes in Playa Carmen, Nicoya Peninsula, Costa Rica. Central location with beach access and local amenities.",
    seoText: "Playa Carmen sits at the heart of the Santa Teresa area, serving as its commercial and social hub. With a wide sandy beach, great waves, and easy access to shops, restaurants, and nightlife, it's the most convenient location on the peninsula.\n\nRenters here enjoy the benefit of walkability, most daily needs are within a short distance. It's ideal for those who prefer an active, connected lifestyle without needing a vehicle.",
  },
  {
    slug: "san-isidro",
    name: "San Isidro",
    shortDescription: "San Isidro is a quiet rural village inland on the Nicoya Peninsula, offering an authentic Costa Rican countryside experience with green hills and local farms.",
    tags: ["Rural", "Quiet", "Local"],
    image: sanIsidroImg,
    coordinates: [-85.1100, 9.7200],
    location: { bullets: ["Inland on the Nicoya Peninsula", "~20–30 min from Santa Teresa", "Between Cóbano and the northern peninsula"] },
    knownFor: ["Authentic rural Costa Rican life", "Green rolling hills and farmland", "Affordable properties with space"],
    bestFor: "Long-term residents seeking rural tranquility",
    vibe: "Rural, peaceful, authentic",
    access: "20–30 min from Santa Teresa",
    seoDescription: "Find rental homes in San Isidro, Nicoya Peninsula, Costa Rica. Rural properties and countryside homes for long-term stays.",
    seoText: "San Isidro is a small rural community located inland on the Nicoya Peninsula, surrounded by green hills, farmland, and tropical countryside. It offers an authentic Costa Rican village experience far removed from the tourist areas along the coast.\n\nProperties in San Isidro tend to be spacious and affordable, making it an excellent choice for long-term residents, families, and anyone seeking a quieter, more grounded lifestyle with easy access to the peninsula's beaches and services.",
  },
  {
    slug: "santa-teresa",
    name: "Santa Teresa",
    shortDescription: "Santa Teresa is a vibrant beach town on the west coast of the Nicoya Peninsula, known for world-class surf, international restaurants, and a strong digital nomad community.",
    tags: ["Surf", "Social", "Walkable"],
    image: santaTeresaImg,
    coordinates: [-85.1680, 9.6400],
    location: { bullets: ["On the west coast of the Nicoya Peninsula", "~20–30 min from Montezuma"] },
    knownFor: ["World-class surf", "International restaurants & cafés", "Strong digital nomad community"],
    bestFor: "Surfers, digital nomads, and social travelers",
    vibe: "Vibrant, international, active",
    access: "Reached via the Nicoya Peninsula's coastal road",
    seoDescription: "Find monthly rental homes in Santa Teresa, Nicoya Peninsula, Costa Rica. Premium villas, surf houses, and apartments for mid-term stays.",
    seoText: "Santa Teresa is one of the most sought-after destinations in Costa Rica, known for its world-class surf, vibrant international community, and relaxed beach lifestyle. Located on the Nicoya Peninsula, it attracts digital nomads, surfers, and travelers looking for a mix of nature and modern comforts.\n\nThe area offers a wide range of rental properties, from luxury beachfront villas to jungle homes. With strong infrastructure, restaurants, and wellness offerings, Santa Teresa is ideal for both short and long-term stays.",
  },
  {
    slug: "santiago",
    name: "Santiago",
    shortDescription: "Santiago is nestled in the hills just above Playa Hermosa, north of Santa Teresa, characterized by stunning ocean views and high-end properties.",
    tags: ["Views", "Nature", "Luxury"],
    image: santiagoImg,
    coordinates: [-85.1715, 9.6770],
    location: { bullets: ["~10–15 min north of Santa Teresa", "In the hills above Playa Hermosa", "Private and natural surroundings"] },
    knownFor: ["Stunning ocean views", "Cool breezes", "High-end properties"],
    bestFor: "Those seeking privacy and ocean views",
    vibe: "Private, natural, elevated",
    access: "10–15 min north of Santa Teresa",
    seoDescription: "Find luxury rental homes in Santiago, Nicoya Peninsula, Costa Rica. High-end properties with ocean views and privacy.",
    seoText: "Santiago is nestled in the hills just above Playa Hermosa, north of Santa Teresa. It is characterized by its stunning ocean views, cool breezes, high-end properties, and lush natural surroundings, a more private and elevated setting away from the busy coastline.",
  },

  // ---- Nosara (nosara) · Guanacaste, Costa Rica ---------------------------
  // Starter content + placeholder imagery; refine per tenant via the Area editor.
  {
    slug: "nosara",
    name: "Nosara",
    shortDescription: "Nosara is a laid-back beach town in Guanacaste known for world-class surf, yoga and wellness retreats, and an eco-conscious community.",
    tags: ["Surf", "Wellness", "Eco"],
    image: placeholderAreaImg,
    coordinates: [-85.6520, 9.9760],
    location: { bullets: ["On the Pacific coast of the Nicoya Peninsula, Guanacaste", "~10 min from Playa Guiones", "Reached via Sámara or the inland road from Nicoya"] },
    knownFor: ["World-class surf", "Yoga & wellness retreats", "Howler monkeys and protected jungle"],
    bestFor: "Surfers, yogis, and remote workers",
    vibe: "Relaxed, wellness-focused, natural",
    access: "Central Nosara, short drive to the main beaches",
    seoDescription: "Find monthly and seasonal rental homes in Nosara, Guanacaste, Costa Rica. Surf houses, jungle villas, and wellness retreats for mid- and long-term stays.",
    seoText: "Nosara is one of Costa Rica's most beloved beach towns, set along the Pacific coast of the Nicoya Peninsula in Guanacaste. It is known for consistent year-round surf, a thriving yoga and wellness scene, and a community committed to protecting its jungle and wildlife.\n\nRental properties range from beachside surf houses to secluded jungle villas, making Nosara a favourite for surfers, remote workers, and anyone seeking a healthy, nature-connected lifestyle.",
  },
  {
    slug: "playa-guiones",
    name: "Playa Guiones",
    shortDescription: "Playa Guiones is Nosara's main surf beach, a long stretch of golden sand with reliable waves and easy access to cafés, shops, and yoga studios.",
    tags: ["Surf", "Beach", "Walkable"],
    image: placeholderAreaImg,
    coordinates: [-85.6680, 9.9420],
    location: { bullets: ["Nosara's main surf beach", "Walking distance to cafés, shops, and studios", "~10 min from Nosara town"] },
    knownFor: ["Consistent, beginner-friendly surf", "A walkable beach community", "Cafés and wellness studios"],
    bestFor: "Surfers and those who want walkable living",
    vibe: "Active, social, beachy",
    access: "Walkable to the beach and the town center",
    seoDescription: "Browse monthly rental homes in Playa Guiones, Nosara, Guanacaste, Costa Rica. Surf houses and villas within walking distance of the beach.",
    seoText: "Playa Guiones is the heart of Nosara's surf scene, a long stretch of golden sand framed by protected jungle. Its consistent, beginner-friendly waves draw surfers of every level, while a walkable cluster of cafés, shops, and yoga studios makes it easy to live without a car.\n\nMost rentals here are a short walk or bike ride from the sand, ideal for surfers and remote workers who want the beach at their doorstep.",
  },
  {
    slug: "playa-pelada",
    name: "Playa Pelada",
    shortDescription: "Playa Pelada is a quieter cove just north of Guiones, known for tide pools, a natural blowhole, and a more local, tranquil atmosphere.",
    tags: ["Quiet", "Beach", "Local"],
    image: placeholderAreaImg,
    coordinates: [-85.6620, 9.9870],
    location: { bullets: ["Just north of Playa Guiones", "A small, calmer cove with tide pools", "Close to Nosara town"] },
    knownFor: ["Calm tide pools and a natural blowhole", "A favourite sunset spot with local sodas", "Quieter than Guiones"],
    bestFor: "Families and those seeking a calmer beach",
    vibe: "Tranquil, local, scenic",
    access: "Short drive from Nosara town and Guiones",
    seoDescription: "Find rental homes in Playa Pelada, Nosara, Guanacaste, Costa Rica. Quiet beachside properties near tide pools and sunsets.",
    seoText: "Playa Pelada is a small, tranquil cove just north of Playa Guiones, beloved for its tide pools, natural blowhole, and laid-back local feel. It is a favourite sunset spot, with a couple of beachfront sodas serving fresh food.\n\nRentals around Pelada tend to be quieter and more residential, a good fit for families and anyone wanting a calmer base within easy reach of Nosara's surf and amenities.",
  },
  {
    slug: "playa-garza",
    name: "Playa Garza",
    shortDescription: "Playa Garza is a calm, crescent-shaped bay south of Nosara, home to a working fishing village and gentle, swimmable water.",
    tags: ["Quiet", "Bay", "Local"],
    image: placeholderAreaImg,
    coordinates: [-85.6430, 9.9170],
    location: { bullets: ["South of Nosara along the coast", "A sheltered bay with calm water", "Anchored by a working fishing village"] },
    knownFor: ["A calm, swimmable bay", "Local fishing-village charm", "Boats and quiet beach days"],
    bestFor: "Families and calm-water lovers",
    vibe: "Sleepy, authentic, peaceful",
    access: "~15–20 min south of Nosara",
    seoDescription: "Find rental homes in Playa Garza, Nosara, Guanacaste, Costa Rica. Quiet bayside properties in a traditional fishing village.",
    seoText: "Playa Garza is a calm, crescent-shaped bay south of Nosara, anchored by a small working fishing village. Its sheltered, gentle water makes it one of the more swimmable beaches in the area and a peaceful alternative to the surf beaches.\n\nProperties here are limited and low-key, appealing to those who want an authentic, quiet stretch of coast within a short drive of Nosara's services.",
  },
  {
    slug: "esperanza",
    name: "Esperanza",
    shortDescription: "Esperanza is a green, inland community near Nosara offering larger lots, privacy, and quick access to the beaches and town.",
    tags: ["Inland", "Quiet", "Spacious"],
    image: placeholderAreaImg,
    coordinates: [-85.6300, 9.9900],
    location: { bullets: ["Inland, near Nosara", "Larger lots and green surroundings", "Short drive to Guiones and town"] },
    knownFor: ["Privacy and space", "Lush, green surroundings", "Value relative to beachfront"],
    bestFor: "Long-term residents wanting space and value",
    vibe: "Green, private, residential",
    access: "Short drive to Nosara's beaches and town",
    seoDescription: "Find rental homes in Esperanza, Nosara, Guanacaste, Costa Rica. Spacious inland properties a short drive from the beaches.",
    seoText: "Esperanza is a green, inland community near Nosara, known for larger lots, privacy, and a quieter pace than the beachfront areas. It offers good value and space while keeping the surf and amenities of Guiones and Nosara town within a short drive.\n\nIt suits long-term residents and families who want room to breathe without giving up easy access to the coast.",
  },
  // ---- Tamarindo (tamarindo) · Guanacaste, Costa Rica ---------------------
  // Card texts from Angelina's document (2026-10-11); long texts from the launch_market() seed (scripts/migrate/new-project-launch-market-2026-10-11.sql).
  {
    slug: "tamarindo",
    name: "Playa Tamarindo",
    shortDescription: "Tamarindo is Guanacaste's best-known beach town, with an easy surf break, restaurants and cafés on every corner, and everything you need for a long stay within walking distance.",
    tags: ["Surf", "Social", "Walkable"],
    image: placeholderAreaImg,
    coordinates: [-85.841, 10.2995],
    location: { bullets: ["On the Pacific coast of Guanacaste", "About 1 hour from Liberia airport", "Town centre along the beach road"] },
    knownFor: ["Beginner-friendly surf", "Restaurants and nightlife", "Sunsets over the bay"],
    bestFor: "People who want everything within walking distance",
    vibe: "Lively, social, beachy",
    access: "Paved road; about 1 hour from Liberia airport",
    seoDescription: "Find monthly and seasonal rental homes in Tamarindo, Guanacaste, Costa Rica. Condos, houses and villas close to the beach for mid- and long-term stays.",
    seoText: "Tamarindo is one of the best-known beach towns on Costa Rica's Pacific coast. Its long, sheltered bay has waves for every level, and the town packs restaurants, cafés, shops and services within walking distance of the sand.\n\nRentals range from condos a block from the beach to houses and villas in the hills behind town, which makes it a practical base for families, remote workers and anyone who wants convenience by the sea.",
  },
  {
    slug: "playa-langosta",
    name: "Playa Langosta",
    shortDescription: "Playa Langosta is a quieter, residential beach just south of Tamarindo town, with an estuary, wide sunsets and a pleasant walk or short bike ride into town.",
    tags: ["Quiet", "Residential", "Sunsets"],
    image: placeholderAreaImg,
    coordinates: [-85.853, 10.286],
    location: { bullets: ["Just south of Tamarindo", "A few minutes' drive or a beach walk at low tide"] },
    knownFor: ["A calmer beach", "Residential streets", "Close to Tamarindo town"],
    bestFor: "Families and people who want quiet near town",
    vibe: "Calm, residential",
    access: "Paved road from Tamarindo",
    seoDescription: "Browse monthly rental homes in Playa Langosta, Tamarindo, Guanacaste. Quiet beach houses and condos minutes from town.",
    seoText: "Playa Langosta sits just south of Tamarindo, separated by a river mouth. It is quieter and more residential, with homes and condos close to a calmer beach, while Tamarindo's restaurants and services are only minutes away.",
  },
  {
    slug: "playa-grande",
    name: "Playa Grande",
    shortDescription: "Playa Grande sits across the estuary north of Tamarindo, inside Las Baulas National Marine Park. It's a long, wild beach with consistent surf, low-density homes, and leatherback turtles nesting in season.",
    tags: ["Nature", "Surf", "Quiet"],
    image: placeholderAreaImg,
    coordinates: [-85.853, 10.33],
    location: { bullets: ["North of Tamarindo across the estuary", "About 20 minutes by road, or a short boat crossing"] },
    knownFor: ["Consistent surf", "Las Baulas National Marine Park", "Leatherback turtle nesting"],
    bestFor: "Surfers and nature lovers",
    vibe: "Natural, low-key",
    access: "About 20 minutes by road from Tamarindo",
    seoDescription: "Find monthly rentals in Playa Grande, Guanacaste: surf houses and homes near Las Baulas National Marine Park.",
    seoText: "Playa Grande is a wide, undeveloped beach protected as part of Las Baulas National Marine Park. It is known for consistent surf and as a nesting site for leatherback turtles, and it stays quiet compared with Tamarindo across the estuary.",
  },
  {
    slug: "villarreal",
    name: "Villarreal",
    shortDescription: "Villarreal is the local town just inland from Tamarindo, with supermarkets, hardware stores and everyday services. Homes here cost noticeably less, and the beach is a short drive away.",
    tags: ["Local", "Practical", "Affordable"],
    image: placeholderAreaImg,
    coordinates: [-85.828, 10.276],
    location: { bullets: ["About 5 minutes inland from Tamarindo"] },
    knownFor: ["Everyday shops and services", "Local Costa Rican community", "Lower long-term prices"],
    bestFor: "Long stays on a budget",
    vibe: "Local, practical",
    access: "5 minutes by car from Tamarindo beach",
    seoDescription: "Browse affordable monthly rental homes in Villarreal, near Tamarindo, Guanacaste, Costa Rica.",
    seoText: "Villarreal is the small town just inland from Tamarindo where many local families live. It has supermarkets, hardware stores and schools, and long-term rentals here usually cost less than in Tamarindo itself, a short drive from the beach.",
  },
  {
    slug: "playa-avellanas",
    name: "Playa Avellanas",
    shortDescription: "Playa Avellanas is a laid-back surf beach south of Tamarindo, known for its several breaks, long sunsets and a relaxed beach-bar scene with no town in the way.",
    tags: ["Surf", "Laid-back", "Beach"],
    image: placeholderAreaImg,
    coordinates: [-85.837, 10.224],
    location: { bullets: ["South of Tamarindo", "About 30 minutes by car"] },
    knownFor: ["Several surf breaks", "A relaxed beach scene", "Jungle and wildlife"],
    bestFor: "Surfers who want quiet",
    vibe: "Laid-back, natural",
    access: "About 30 minutes from Tamarindo, partly on dirt roads",
    seoDescription: "Find monthly rental homes near Playa Avellanas, south of Tamarindo, Guanacaste, Costa Rica.",
    seoText: "Playa Avellanas is a quiet surf beach south of Tamarindo, with several breaks along a wide stretch of sand. Homes are spread out among the trees, and the area suits people who want surf and nature over nightlife.",
  },

  // ---- Barcelona (barcelona) · Catalonia, Spain ---------------------------
  // Starter content + placeholder imagery; refine per tenant via the Area editor.
  {
    slug: "eixample",
    name: "Eixample",
    shortDescription: "Eixample is Barcelona's elegant grid district, known for Modernista architecture, wide avenues, and central, walkable living.",
    tags: ["Central", "Architecture", "Walkable"],
    image: placeholderAreaImg,
    coordinates: [2.1620, 41.3880],
    location: { bullets: ["Central Barcelona", "A Modernista grid of wide avenues", "Excellent metro connections"] },
    knownFor: ["Gaudí landmarks like La Sagrada Família and Casa Batlló", "Shopping along Passeig de Gràcia", "Wide, walkable streets"],
    bestFor: "Those wanting central, connected city living",
    vibe: "Elegant, lively, cosmopolitan",
    access: "Central, superb metro and walkability",
    seoDescription: "Browse monthly rental flats in Eixample, Barcelona, Catalonia, Spain. Modernista apartments in the city's central, walkable grid.",
    seoText: "Eixample is Barcelona's grand 19th-century expansion, laid out as an airy grid of wide avenues and chamfered corners. It is the showcase of Catalan Modernisme, home to Gaudí masterpieces and tree-lined shopping boulevards.\n\nApartments here put renters in the heart of the city with excellent metro links and everything walkable, ideal for those who want central, connected living.",
  },
  {
    slug: "gracia",
    name: "Gràcia",
    shortDescription: "Gràcia is a bohemian former village with narrow streets, lively plazas, and an independent, neighbourhood feel.",
    tags: ["Bohemian", "Plazas", "Local"],
    image: placeholderAreaImg,
    coordinates: [2.1570, 41.4030],
    location: { bullets: ["North of Eixample", "A maze of narrow streets and small squares", "An independent, village-like feel"] },
    knownFor: ["Lively plazas and terraces", "Independent shops and bars", "The Festa Major de Gràcia"],
    bestFor: "Creatives and those wanting a neighbourhood vibe",
    vibe: "Bohemian, social, independent",
    access: "Well connected by metro to the centre",
    seoDescription: "Find monthly rental flats in Gràcia, Barcelona, Catalonia, Spain. Apartments in a bohemian, plaza-filled neighbourhood.",
    seoText: "Gràcia was an independent town until the late 1800s, and it still feels like one, a warren of narrow streets opening onto lively plazas full of terraces. It is known for its independent shops, creative community, and the riotous Festa Major each August.\n\nFlats here suit renters who want a neighbourly, characterful base with the city centre just a short metro ride away.",
  },
  {
    slug: "el-born",
    name: "El Born",
    shortDescription: "El Born is a historic, fashionable quarter of medieval lanes, the Picasso Museum, and buzzing tapas bars.",
    tags: ["Historic", "Trendy", "Nightlife"],
    image: placeholderAreaImg,
    coordinates: [2.1830, 41.3850],
    location: { bullets: ["In the Ciutat Vella (old city)", "Medieval lanes near the waterfront", "Walking distance to the Gothic Quarter and beach"] },
    knownFor: ["The Picasso Museum and Santa Maria del Mar", "Tapas bars and boutiques", "Lively nightlife"],
    bestFor: "Those wanting historic charm and nightlife",
    vibe: "Historic, fashionable, vibrant",
    access: "Central old city, very walkable",
    seoDescription: "Browse monthly rental flats in El Born, Barcelona, Catalonia, Spain. Apartments among medieval lanes, museums, and tapas bars.",
    seoText: "El Born is one of Barcelona's most atmospheric quarters, a tangle of medieval lanes in the old city dotted with boutiques, tapas bars, and landmarks like the Picasso Museum and the basilica of Santa Maria del Mar.\n\nIts central location puts the Gothic Quarter, waterfront, and beach within walking distance, ideal for renters who want history and nightlife on the doorstep.",
  },
  {
    slug: "barceloneta",
    name: "Barceloneta",
    shortDescription: "Barceloneta is Barcelona's beachfront neighbourhood, a former fishermen's quarter with seafood restaurants and the city's most popular beach.",
    tags: ["Beach", "Seafood", "Lively"],
    image: placeholderAreaImg,
    coordinates: [2.1890, 41.3800],
    location: { bullets: ["On the waterfront, east of the old city", "Home to the city's main beach", "Walking distance to the port and El Born"] },
    knownFor: ["The city beach and seaside promenade", "Traditional seafood restaurants", "Sailing and water sports"],
    bestFor: "Beach lovers who want to be in the city",
    vibe: "Seaside, social, traditional",
    access: "Walkable to the beach; metro to the centre",
    seoDescription: "Find monthly rental flats in Barceloneta, Barcelona, Catalonia, Spain. Beachfront apartments steps from the sand and seafood.",
    seoText: "Barceloneta is the city's beachfront neighbourhood, a tight grid of narrow streets built for fishermen and now famous for its seafood restaurants and long stretch of sand.\n\nLiving here means the Mediterranean at your doorstep and the old city a short walk away, a fit for renters who want beach days without leaving Barcelona.",
  },
  {
    slug: "sant-antoni",
    name: "Sant Antoni",
    shortDescription: "Sant Antoni is a trendy, increasingly popular district known for its renovated market, vermouth bars, and a relaxed local feel.",
    tags: ["Trendy", "Local", "Foodie"],
    image: placeholderAreaImg,
    coordinates: [2.1620, 41.3790],
    location: { bullets: ["West of El Raval, bordering Eixample", "Centred on the Sant Antoni market", "Well connected and walkable"] },
    knownFor: ["The beautifully restored Sant Antoni market", "Vermouth and tapas bars", "A laid-back, local scene"],
    bestFor: "Foodies wanting a local, central base",
    vibe: "Trendy, relaxed, local",
    access: "Central and walkable; good metro links",
    seoDescription: "Browse monthly rental flats in Sant Antoni, Barcelona, Catalonia, Spain. Apartments around the market in a trendy, local district.",
    seoText: "Sant Antoni has become one of Barcelona's most sought-after neighbourhoods, anchored by its beautifully restored market hall and a Sunday-morning ritual of vermouth on sunny terraces.\n\nIt offers a relaxed, local atmosphere within walking distance of the centre, appealing to renters who want great food and an authentic neighbourhood feel.",
  },
  {
    slug: "poblenou",
    name: "Poblenou",
    shortDescription: "Poblenou is a former industrial district reborn as Barcelona's creative and tech hub, with lofts, beaches, and a leafy rambla.",
    tags: ["Creative", "Modern", "Beach"],
    image: placeholderAreaImg,
    coordinates: [2.2040, 41.4030],
    location: { bullets: ["East of the centre, near the coast", "The 22@ innovation district", "Beaches and the Rambla del Poblenou"] },
    knownFor: ["Tech and design studios", "Converted industrial lofts", "Quieter beaches than the centre"],
    bestFor: "Remote workers and creatives",
    vibe: "Creative, modern, easy-going",
    access: "Metro and tram; walkable to the beach",
    seoDescription: "Find monthly rental flats in Poblenou, Barcelona, Catalonia, Spain. Loft-style apartments in the city's creative, seaside district.",
    seoText: "Poblenou traded its industrial past for a creative future, becoming the heart of Barcelona's 22@ tech and design district. Old factories are now lofts and studios, and a leafy rambla leads down to quieter beaches.\n\nIt is a favourite for remote workers and creatives who want modern space, a relaxed pace, and the sea close by.",
  },
  {
    slug: "gotic",
    name: "Gòtic",
    shortDescription: "The Gothic Quarter is Barcelona's medieval core, a labyrinth of stone lanes, hidden squares, and Roman remains at the city's heart.",
    tags: ["Historic", "Central", "Walkable"],
    image: placeholderAreaImg,
    coordinates: [2.1770, 41.3830],
    location: { bullets: ["The historic centre of the old city", "Beside La Rambla and the cathedral", "Entirely walkable"] },
    knownFor: ["Medieval streets and Roman walls", "The Barcelona Cathedral", "Plaças and centuries-old shops"],
    bestFor: "Those who want to live in the historic heart",
    vibe: "Historic, atmospheric, central",
    access: "Dead-central and pedestrian-friendly",
    seoDescription: "Browse monthly rental flats in the Gothic Quarter, Barcelona, Catalonia, Spain. Apartments in the medieval heart of the city.",
    seoText: "The Gothic Quarter is the ancient core of Barcelona, a labyrinth of narrow stone lanes that open onto hidden squares, with Roman walls and a soaring medieval cathedral at its centre.\n\nLiving here means being in the historic heart of the city, steps from La Rambla and the waterfront, best for those who want atmosphere and central walkability over quiet.",
  },
  {
    slug: "sarria",
    name: "Sarrià",
    shortDescription: "Sarrià is a refined, residential uptown neighbourhood with a village core, leafy streets, and a calm, family-friendly feel.",
    tags: ["Residential", "Quiet", "Upscale"],
    image: placeholderAreaImg,
    coordinates: [2.1210, 41.3990],
    location: { bullets: ["Uptown, in the foothills", "A preserved old village centre", "Close to schools and parks"] },
    knownFor: ["Quiet, leafy streets", "A village-like old centre", "Family-friendly living"],
    bestFor: "Families seeking a calm, upscale base",
    vibe: "Refined, residential, peaceful",
    access: "FGC trains and metro to the centre",
    seoDescription: "Find monthly rental flats in Sarrià, Barcelona, Catalonia, Spain. Apartments in a calm, upscale uptown neighbourhood.",
    seoText: "Sarrià is a refined uptown neighbourhood that still keeps the feel of the village it once was, with a quiet old centre, leafy streets, and a strong sense of community.\n\nIt is one of the calmest, most family-friendly parts of Barcelona, well connected to the centre by train, a fit for renters who prioritise space and tranquility.",
  },
];

export function getAreaBySlug(slug: string): AreaData | undefined {
  return areas.find((a) => a.slug === slug);
}
